<script setup lang="ts">
import { type FormSubmitEvent } from '#ui/types';
import { useQuery } from '@tanstack/vue-query';
import lodashPick from 'lodash-es/pick';
import { computed, unref } from 'vue';

import {
    apiAuctionsListingBrands,
    apiAuctionsListingModels,
    apiDirectSalesListingBrands,
    apiDirectSalesListingModels,
    getApiAuctionsListingQueryKey,
    AssetEuroNorm,
    AssetType,
    getApiDirectSalesListingQueryKey,
    SellingMethod,
    useApiAuctionsListing,
    useApiDirectSalesListing,
} from '~/apiClient';
import {
    EMPTY_OPTION_VALUE,
    FILTER_PRICE_END,
    FILTER_PRICE_START,
    FILTER_PRICE_STEP,
    FILTER_REGISTRATION_YEAR_END,
    FILTER_REGISTRATION_YEAR_START,
    FILTER_REGISTRATION_YEAR_STEP,
} from '~/constants/filters';
import { PlatformBusClass } from '~/types/asset.type';
import { AlpFilterableState, DslpFilterableState } from '~/types/filters';
import type { PlatformSellingMethod } from '~/types/sale.type';

// PROPS
export interface StoryblokSearchFormProps {
    blok: StoryblokSearchForm;
}

const { blok } = defineProps<StoryblokSearchFormProps>();

// SCHEMA
type FormData = {
    sellingMethod: PlatformSellingMethod;
    platformClass?: PlatformBusClass[];
    euroNorm?: AssetEuroNorm[];
    brandUrlKey?: string;
    modelUrlKey?: string;
    firstRegistrationYearFrom?: typeof EMPTY_OPTION_VALUE | number;
    priceTo?: typeof EMPTY_OPTION_VALUE | number;
};

const formData = reactive<FormData>({
    sellingMethod: SellingMethod.DirectSale,
    platformClass: undefined,
    brandUrlKey: undefined,
    modelUrlKey: undefined,
    firstRegistrationYearFrom: EMPTY_OPTION_VALUE,
    euroNorm: undefined,
    priceTo: EMPTY_OPTION_VALUE,
});

// COMPOSABLES
const { tt, tn } = useTypedI18n();
const { formatSlpRoute } = useSlpRouteUtils();
const localePath = useLocalePath();

// DATA
const isMounted = ref(false);

onMounted(() => {
    isMounted.value = true;
});

const apiParams = computed(() => ({
    ...(formData.platformClass ? { class: getBusClassesFromPlatformBusClasses(formData.platformClass) } : undefined),
    euroNorm: formData.euroNorm ? formData.euroNorm : undefined,
    brandUrlKey: formData.brandUrlKey,
    modelUrlKey: formData.modelUrlKey,
    firstRegistrationYearFrom:
        formData.firstRegistrationYearFrom && formData.firstRegistrationYearFrom !== EMPTY_OPTION_VALUE
            ? formData.firstRegistrationYearFrom
            : undefined,
    priceTo: formData.priceTo && formData.priceTo !== EMPTY_OPTION_VALUE ? formData.priceTo : undefined,
    itemsPerPage: 0,
}));
const auctionsApiParams = computed(() => ({ ...apiParams.value, state: [AlpFilterableState.Running] }));
const { data: alpData, isPending: isAlpPending } = useApiAuctionsListing(auctionsApiParams, {
    query: {
        enabled: computed(() => isMounted.value && formData.sellingMethod === SellingMethod.Auction),
        queryKey: getApiAuctionsListingQueryKey(auctionsApiParams),
    },
});
const directSalesApiParams = computed(() => ({ ...apiParams.value, state: [DslpFilterableState.Open] }));

const { data: dslpData, isPending: isDslpPending } = useApiDirectSalesListing(directSalesApiParams, {
    query: {
        enabled: computed(() => isMounted.value && formData.sellingMethod === SellingMethod.DirectSale),
        queryKey: getApiDirectSalesListingQueryKey(directSalesApiParams),
    },
});

const count = computed(() => (formData.sellingMethod === SellingMethod.Auction ? alpData.value?.totalItems : dslpData.value?.totalItems));
const isLoadingCount = computed(() => (formData.sellingMethod === SellingMethod.Auction ? isAlpPending.value : isDslpPending.value));
const brandId = ref<string | undefined>();
const { data: brands, isLoading: isLoadingBrands } = useQuery({
    queryKey: [computed(() => formData.sellingMethod), 'brands'],
    enabled: isMounted,
    queryFn: async () => (formData.sellingMethod === SellingMethod.Auction ? apiAuctionsListingBrands() : apiDirectSalesListingBrands()),
});
const { data: models, isLoading: isLoadingModels } = useQuery({
    queryKey: [computed(() => formData.sellingMethod), 'models', brandId],
    enabled: computed(() => !!unref(brandId)),
    queryFn: async () =>
        formData.sellingMethod === SellingMethod.Auction ? apiAuctionsListingModels(brandId) : apiDirectSalesListingModels(brandId),
});
const platformSellingMethodValues: PlatformSellingMethod[] = [SellingMethod.Auction, SellingMethod.DirectSale];
const sellingMethodOptions = platformSellingMethodValues.map(value => ({
    value,
    label: tt(`storyblok.searchForm.fields.sellingMethod.options.${value}`),
}));
const platformClassOptions = Object.values(PlatformBusClass).map(value => ({ value, label: tt(`asset.class.${value}`) }));
const euroNormOptions = Object.values(AssetEuroNorm).map(value => ({ value, label: tt(`asset.euroNorm.${value}`) }));
const brandUrlKeyOptions = computed(() =>
    brands.value && !isLoadingBrands.value
        ? brands.value.brands
              .map(b => ({
                  label: b.brandValue,
                  value: b.urlKey,
              }))
              .sort((a, b) => a.label.localeCompare(b.label))
        : []
);
const modelUrlKeyOptions = computed(() =>
    models.value && !isLoadingModels.value
        ? models.value.models
              .map(m => ({
                  label: m.modelValue,
                  value: m.urlKey,
              }))
              .sort((a, b) => a.label.localeCompare(b.label))
        : []
);
const firstRegistrationYearFromOptions = prependEmptyOption(
    range(FILTER_REGISTRATION_YEAR_START, FILTER_REGISTRATION_YEAR_END, FILTER_REGISTRATION_YEAR_STEP).map(value => ({
        value,
        label: String(value),
    })),
    tt('slp.filters.from')
);
const priceToOptions = prependEmptyOption(
    range(FILTER_PRICE_START, FILTER_PRICE_END, FILTER_PRICE_STEP).map(value => ({ value, label: tn(value, 'currency') })),
    tt('slp.filters.to')
);
const formFieldUi = { label: 'truncate' };

// HANDLERS
const onSelectSellingMethod = (): void => {
    brandId.value = undefined;
    formData.brandUrlKey = undefined;
    formData.modelUrlKey = undefined;
};

const onSelectBrand = (urlKey: string | null): void => {
    brandId.value = brands.value?.brands.find(b => b.urlKey === urlKey)?.id;
    formData.modelUrlKey = undefined;
};

const onSubmit = (event: FormSubmitEvent<FormData>): void => {
    navigateTo(
        localePath(
            formatSlpRoute(
                {
                    assetType: AssetType.Bus,
                    platformClass: event.data.platformClass && event.data.platformClass.length > 0 ? event.data.platformClass[0] : undefined,
                    ...lodashPick(event.data, ['sellingMethod', 'brandUrlKey', 'modelUrlKey']),
                },
                {
                    ...(event.data.platformClass && event.data.platformClass?.length > 1
                        ? { [SLP_QUERY_KEYS.platformClass]: event.data.platformClass.slice(1) }
                        : undefined),
                    ...(event.data.firstRegistrationYearFrom && event.data.firstRegistrationYearFrom !== EMPTY_OPTION_VALUE
                        ? { [SLP_QUERY_KEYS.firstRegistrationYearFrom]: event.data.firstRegistrationYearFrom }
                        : undefined),
                    ...(event.data.euroNorm ? { [SLP_QUERY_KEYS.euroNorm]: event.data.euroNorm } : undefined),
                    ...(event.data.priceTo && event.data.priceTo !== EMPTY_OPTION_VALUE
                        ? { [SLP_QUERY_KEYS.priceTo]: event.data.priceTo }
                        : undefined),
                }
            )
        )
    );
};
</script>

<template>
    <div v-editable="blok" class="w-full space-y-4">
        <h4 class="text-lg font-bold">{{ tt('storyblok.searchForm.title') }}</h4>
        <UForm :state="formData" class="grid grid-cols-2 gap-2" @submit="onSubmit">
            <URadioGroup
                v-model="formData.sellingMethod"
                :ui="{ item: 'p-2' }"
                name="sellingMethod"
                variant="card"
                orientation="horizontal"
                :items="sellingMethodOptions"
                class="col-span-2"
                @update:model-value="onSelectSellingMethod"
            />
            <UFormField :label="tt('slp.filters.class')" name="platformClass" :ui="formFieldUi">
                <USelectMenu v-model="formData.platformClass" multiple :search-input="false" :items="platformClassOptions" value-key="value" clear />
            </UFormField>
            <UFormField :label="tt('slp.filters.euroNorm')" name="euroNorm" :ui="formFieldUi">
                <USelectMenu v-model="formData.euroNorm" multiple :search-input="false" :items="euroNormOptions" value-key="value" clear />
            </UFormField>
            <UFormField :label="tt('slp.filters.brandFilter')" name="brandUrlKey" :ui="formFieldUi">
                <USelectMenu
                    v-model="formData.brandUrlKey"
                    :items="brandUrlKeyOptions"
                    value-key="value"
                    :loading="isLoadingBrands"
                    clear
                    @update:model-value="onSelectBrand"
                />
            </UFormField>
            <UFormField :label="tt('slp.filters.modelFilter')" name="modelUrlKey" :ui="formFieldUi">
                <USelectMenu
                    v-model="formData.modelUrlKey"
                    :items="modelUrlKeyOptions"
                    :disabled="!brandId"
                    value-key="value"
                    :loading="!!brandId && isLoadingModels"
                    clear
                />
            </UFormField>
            <UFormField :label="tt('slp.filters.firstRegistrationYear')" name="firstRegistrationYearFrom" :ui="formFieldUi">
                <USelectMenu
                    v-model="formData.firstRegistrationYearFrom"
                    :search-input="false"
                    :items="firstRegistrationYearFromOptions"
                    value-key="value"
                />
            </UFormField>
            <UFormField :label="tt('slp.filters.price')" name="priceTo" :ui="formFieldUi">
                <USelectMenu v-model="formData.priceTo" :search-input="false" :items="priceToOptions" value-key="value" />
            </UFormField>
            <UButton size="xl" block type="submit" color="primary" class="col-span-2 mt-4 md:col-span-1 md:col-start-2">
                <BaseI18nT keypath="storyblok.searchForm.submit" tag="span" :plural="count">
                    <template #count>
                        <UIcon v-if="isLoadingCount" name="i-svg-spinners-270-ring" class="h-4 w-4" />
                        <strong v-else>{{ count }}</strong>
                    </template>
                </BaseI18nT>
            </UButton>
        </UForm>
    </div>
</template>
